import { useState } from 'react';
import { TableProperties, TriangleAlert } from 'lucide-react';
import type { CustomFieldSchema, DomainRule, SortDir } from '@contracts';
import { DomainBuilder } from '@/components/DomainBuilder';
import { Field, SELECT } from '@/components/wizard/WizardUI';

const NOISE = new Set(['company_id']); // estructural, no se ofrece como columna

/** Paso "Detalle": qué se ve AL ABRIR el widget. Hereda los filtros del cálculo; aquí
 *  solo se configura la presentación (columnas/orden/límite) y, avanzado, filtros extra. */
export function CustomDetailStep({
  fields,
  columns,
  sortBy,
  sortDir,
  limit,
  groupBy,
  extraDomain,
  onColumnsChange,
  onSortByChange,
  onSortDirChange,
  onLimitChange,
  onGroupByChange,
  onExtraDomainChange,
}: {
  fields: CustomFieldSchema[];
  columns: string[];
  sortBy: string;
  sortDir: SortDir;
  limit: number | '';
  groupBy: string;
  extraDomain: DomainRule[];
  onColumnsChange: (c: string[]) => void;
  onSortByChange: (f: string) => void;
  onSortDirChange: (d: SortDir) => void;
  onLimitChange: (n: number | '') => void;
  onGroupByChange: (f: string) => void;
  onExtraDomainChange: (d: DomainRule[]) => void;
}) {
  const [showAdvanced, setShowAdvanced] = useState(extraDomain.length > 0);
  const allNames = fields.map((f) => f.name).filter((n) => !NOISE.has(n));
  const isDefault = columns.length === 0; // [] = todas (default)
  const isChecked = (name: string) => isDefault || columns.includes(name);

  const toggle = (name: string) => {
    const base = isDefault ? allNames : columns;
    const next = base.includes(name) ? base.filter((n) => n !== name) : [...base, name];
    if (next.length === 0) return; // al menos una columna
    onColumnsChange(next.length === allNames.length ? [] : next); // todas → default
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
        <TableProperties className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <span>
          El detalle muestra <b>los registros que componen el número</b>, con los mismos filtros del
          cálculo. Aquí eliges cómo se ven.
        </span>
      </div>

      <Field label="Columnas a mostrar">
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
          {fields
            .filter((f) => !NOISE.has(f.name))
            .map((f) => (
              <label
                key={f.name}
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm hover:border-red-300 has-[:checked]:border-red-400 has-[:checked]:bg-red-50/60"
              >
                <input type="checkbox" className="accent-red-600" checked={isChecked(f.name)} onChange={() => toggle(f.name)} />
                <span className="truncate text-slate-700">{f.label}</span>
              </label>
            ))}
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Ordenar por">
          <select value={sortBy} onChange={(e) => onSortByChange(e.target.value)} className={SELECT}>
            <option value="">— por default —</option>
            {fields.map((f) => (
              <option key={f.name} value={f.name}>
                {f.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Dirección">
          <div className="flex gap-2">
            {(['desc', 'asc'] as const).map((d) => (
              <button
                key={d}
                onClick={() => onSortDirChange(d)}
                disabled={!sortBy}
                className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-40 ${
                  sortDir === d ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {d === 'desc' ? 'Mayor→menor' : 'Menor→mayor'}
              </button>
            ))}
          </div>
        </Field>
      </div>

      <Field label="Agrupar por (opcional → encabezado + subtotales)">
        <select value={groupBy} onChange={(e) => onGroupByChange(e.target.value)} className={SELECT}>
          <option value="">— sin agrupar —</option>
          {fields
            .filter((f) => !NOISE.has(f.name) && f.type !== 'number')
            .map((f) => (
              <option key={f.name} value={f.name}>
                {f.label}
              </option>
            ))}
        </select>
      </Field>

      <Field label="Máximo de filas (opcional)">
        <input
          type="number"
          min={1}
          max={1000}
          value={limit}
          onChange={(e) => onLimitChange(e.target.value ? Math.max(1, Math.min(1000, Number(e.target.value))) : '')}
          placeholder="200 (default)"
          className="w-40 rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-red-600"
        />
      </Field>

      <div>
        <button
          onClick={() => setShowAdvanced((s) => !s)}
          className="text-xs font-semibold text-slate-400 hover:text-slate-700"
        >
          {showAdvanced ? '▾' : '▸'} Filtros adicionales del detalle (avanzado)
        </button>
        {showAdvanced && (
          <div className="mt-2 space-y-2">
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-700">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Estos filtros aplican <b>solo al detalle</b>, así que el interior podría no coincidir con el
                total del widget. Útil para afinar qué ves aquí sin cambiar el número de afuera.
              </span>
            </div>
            <DomainBuilder fields={fields} domain={extraDomain} onChange={onExtraDomainChange} />
          </div>
        )}
      </div>
    </div>
  );
}
